import React, { useState, useEffect, useRef } from 'react';
import { Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiCall from './../../api';
import {
    fetchPersonalData,
    updatePersonalData,
    updateBankData,
    removeBankAccount,
    fetchTaxInformation,
    updateTaxInformation,
    cancelTaxRequest,
    fetchUserPreferences,
    updateUserPreferences,
    resetMessages,
    type PersonalData,
} from '../features/profile-settings/profile-settings.slice';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/header';
import SetPasswordModal from '../components/SetPasswordModal';
import DeactivateAccountModal from '../components/DeactivateAccountModal';
import DeleteAccountModal from '../components/DeleteAccountModal';
import RemoveBankAccountModal from '../components/RemoveBankAccountModal';
import '../scss/profile-settings.style.scss';
import { InvoicePreview } from '../components/invoice';
import { getItem } from '../helper/localstorage.helper';
import { renderCountryOptionsWithI18n, getCountryTranslationKey } from '../utils/countries';
import CalendarSingle from '../components/calendar/calendar-single.component';
import { FormField } from '../components/common/form-field.component';
import useOutsideClick from '../hooks/useOutsideClick';
import { isGuestInvozioEmail } from '../helper/common.helper';

type TabType = 'profile' | 'bank' | 'preferences';

export const ProfileSettingsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch() as any;
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Redux selectors
    const user = useSelector((state: any) => state.auth.user);
    const {
        personalData,
        taxInformation,
        userPreferences,
        isLoading,
    } = useSelector((state: any) => state.profileSettings);
    const [formData, setFormData] = useState<Partial<PersonalData>>({});
    const [activeTab, setActiveTab] = useState<TabType>('profile');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRemoveBankModalOpen, setIsRemoveBankModalOpen] = useState(false);
    const [isBankEditMode, setIsBankEditMode] = useState(false);
    const [isTaxEditMode, setIsTaxEditMode] = useState(false);
    const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
    const [OTPField, setOTPField] = useState(new Array(4).fill(''));
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpCounter, setOtpCounter] = useState(300);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] = useState(false);
    const [isEmailOTPModalOpen, setIsEmailOTPModalOpen] = useState(false);
    const [emailToVerify, setEmailToVerify] = useState('');
    const [emailOTPField, setEmailOTPField] = useState(new Array(4).fill(''));
    const [emailOtpCounter, setEmailOtpCounter] = useState(300);
    const [emailOtpLoading, setEmailOtpLoading] = useState(false);
    const [isOldEmailVerified, setIsOldEmailVerified] = useState(false);
    const [emailVerificationStep, setEmailVerificationStep] = useState<'enter-email' | 'verify-old-email' | 'verify-new-email'>('enter-email');
    const emailInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [taxAdjustmentFile, setTaxAdjustmentFile] = useState<File | null>(null);
    const [originalTaxRate, setOriginalTaxRate] = useState<string>('');
    const [taxRateOption, setTaxRateOption] = useState<'yes' | 'no'>();
    const [bankName, setBankName] = useState(personalData.bank || '');
    const [customBankName, setCustomBankName] = useState(personalData.bank || '');
    const [bicNumber, setBicNumber] = useState(personalData.bicNumber || '');
    const [ibanNumber, setIbanNumber] = useState(personalData.ibanNumber || '');
    const [clearingNumber, setClearingNumber] = useState(personalData.clearingNumber || '');
    const [accountNumber, setAccountNumber] = useState(personalData.accountNumber || '');
    const [taxRate, setTaxRate] = useState(personalData.incomeTax);
    const [organizationName, setOrganizationName] = useState(userPreferences?.organizationName || '');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
    const [logoImageSrc, setLogoImageSrc] = useState<string | null>(null);
    const [preferencesArray, setPreferencesArray] = useState<Array<{ key: string; value: any }>>([]);
    const [showDOBCalendar, setShowDOBCalendar] = useState(false);
    const lang = window.location.search.split('lang')[1];
    const [taxRequest, setTaxRequest] = useState<string>(taxInformation?.requestStatus?.toLowerCase() || '');
    const dropdownRef = useOutsideClick(() => setShowDOBCalendar(false)) as React.MutableRefObject<HTMLDivElement | null>;
        const [taxDisclaimerChecked, setTaxDisclaimerChecked] = useState(false);
        const [taxError, setTaxError] = useState("");


    useEffect(() => {
        if (personalData) {
            setBankName(personalData.bank || '');
            setCustomBankName(personalData.bank || '');
            setClearingNumber(personalData.clearingNumber || '');
            setAccountNumber(personalData.accountNumber || '');
            setIbanNumber(personalData.ibanNumber || '');
            setBicNumber(personalData.bicNumber || '');
            setTaxRate(personalData.incomeTax || '30');
        }
    }, [personalData]);
    const handleValueChange = (field: keyof PersonalData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSelectChange = (field: keyof PersonalData, value: string) => {
        handleValueChange(field, value);
    };

    const normalizeCurrencies = (val: any): string[] => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed.startsWith('[')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    return Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                }
            }
            return trimmed.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
        }
        return [];
    };
    useEffect(() => {
        if (userPreferences) {
            setOrganizationName(userPreferences?.organizationName || '');
            setSelectedCurrencies(normalizeCurrencies(userPreferences?.currencies || []));
        }
    }, [userPreferences]);

    useEffect(() => {
        const fetchLogoImage = async () => {
            if (user?.userId) {
                try {
                    const baseUrl = (import.meta as any).env.VITE_API_URL;
                    const token = getItem('token');

                    const response = await fetch(`${baseUrl}/signup/invoice-logo-stream?userId=${user.userId}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json, text/plain, */*'
                        }
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        if (blob.size > 0) {
                            const imageUrl = URL.createObjectURL(blob);
                            setLogoImageSrc(imageUrl);
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch logo:', error);
                }
            }
        };

        fetchLogoImage();
        return () => {
            if (logoImageSrc) {
                URL.revokeObjectURL(logoImageSrc);
            }
        };
    }, [user?.userId]);


    useEffect(() => {
        const arr = [
            { key: 'organizationName', value: organizationName },
            { key: 'logo', value: logoFile ? logoFile.name : userPreferences?.logo || null },
            { key: 'selectedCurrencies', value: Array.isArray(selectedCurrencies) ? selectedCurrencies : [] },
            { key: 'userId', value: user?.userId ?? null },
            { key: 'preferencesSource', value: userPreferences ?? null }
        ];
        setPreferencesArray(arr);
    }, [organizationName, logoFile, selectedCurrencies, userPreferences, user?.userId]);

    const handleBack = () => {
        navigate(`/v2/dashboard?lang${lang}`);
    };

    const handleEditProfile = () => {
        setIsEditMode(true);
    };

    const handleSaveChanges = () => {
        handleSaveProfile();
    };

    const handleSaveProfile = () => {
        // Validate required fields for all profile fields
        if (!formData.firstName || !formData.lastName) {
            toast.error(t('FIRST_NAME_REQUIRED'));
            return;
        }
        if (!formData.phoneNo) {
            toast.error(t('INVALID_PHONE_NUMBER'));
            return;
        }
        if (!formData.address) {
            toast.error(t('ADDRESS_REQUIRED'));
            return;
        }
        if (!formData.country) {
            toast.error(t('COUNTRY_REQUIRED'));
            return;
        }
        if (!formData.city) {
            toast.error(t('CITY_REQUIRED'));
            return;
        }
        if (!formData.zipCode) {
            toast.error(t('POSTAL_CODE_REQUIRED'));
            return;
        }
        if (!formData.profession) {
            toast.error(t('PROFESSION_REQUIRED'));
            return;
        }
        if (personalData?.ssn && !formData.ssn) {
            toast.error(t('SSN_REQUIRED'));
            return;
        }
        if (!formData.dateOfBirth && !personalData?.ssn) {
            toast.error(t('DATE_OF_BIRTH_REQUIRED'));
            return;
        }
        if (user?.userId) {
            const updatedData = {
                ...personalData,
                ...formData
            };
            dispatch(updatePersonalData({
                userData: { id: user?.userId, ...updatedData }
            })).then(() => {
                dispatch(fetchPersonalData(user.userId))
            });
            setFormData({});
            setIsEditMode(false);
        }
    };
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setFormData({});
    };

    const handleDeactivateAccount = () => {
        setIsDeactivateModalOpen(false);
    };

    const handleDeleteAccount = () => {
        setIsDeleteModalOpen(false);
    };
    const getFieldsToShow = () => {
        const isForeignBankSelected = bankName?.toLowerCase().includes('foreign') ||
            bankName?.toLowerCase().includes('bic') ||
            bankName?.toLowerCase().includes('iban') ||
            bankName?.toLowerCase().includes('revolut');
        const isOtherBankSelected = bankName?.toLowerCase().includes('other');
        const isSwedishBankSelected = !isForeignBankSelected && !isOtherBankSelected && bankName?.trim().length > 0;
        const showOtherBankName = isOtherBankSelected;

        return {
            isForeignBankSelected,
            isSwedishBankSelected,
            isOtherBankSelected,
            showOtherBankName
        };
    };

    const validateBankFields = (): boolean => {
        const { isForeignBankSelected, isOtherBankSelected } = getFieldsToShow();

        if (!bankName?.trim()) {
            toast.error((t('PLEASE_SELECT_BANK') || 'Please select a bank') as string);
            return false;
        }

        if (isOtherBankSelected) {
            if (!bankName?.trim()) {
                toast.error((t('PLEASE_ENTER_BANK_NAME') || 'Please enter bank name') as string);
                return false;
            }
        } else if (isForeignBankSelected) {
            if (!ibanNumber?.trim()) {
                toast.error((t('PLEASE_ENTER_IBAN') || 'Please enter IBAN number') as string);
                return false;
            }
            if (!bicNumber?.trim()) {
                toast.error((t('PLEASE_ENTER_BIC') || 'Please enter BIC number') as string);
                return false;
            }
        } else {
            if (!clearingNumber?.trim()) {
                toast.error((t('PLEASE_ENTER_CLEARING') || 'Please enter clearing number') as string);
                return false;
            }
            if (!accountNumber?.trim()) {
                toast.error((t('PLEASE_ENTER_ACCOUNT') || 'Please enter account number') as string);
                return false;
            }

            if (!/^\d{4,5}$/.test(clearingNumber)) {
                toast.error((t('INVALID_CLEARING_FORMAT') || 'Clearing number must be 4-5 digits') as string);
                return false;
            }
            if (!/^\d{6,}$/.test(accountNumber)) {
                toast.error((t('INVALID_ACCOUNT_FORMAT') || 'Account number must be at least 6 digits') as string);
                return false;
            }
        }

        return true;
    };

    const handleBankEditDetails = () => {
        if (isGuestInvozioEmail(personalData.email)) {
            toast.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
            return
        }
        setIsBankEditMode(true);
    };


    const handleBankSaveChanges = () => {
        if (isGuestInvozioEmail(personalData.email)) {
            toast.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
            return;
        }
        // Required field validation for bank edit
        if (!bankName?.trim()) {
            toast.error(t('PLEASE_SELECT_BANK'));
            return;
        }
        if (getFieldsToShow().isOtherBankSelected && !customBankName?.trim()) {
            toast.error(t('PLEASE_ENTER_BANK_NAME'));
            return;
        }
        if (getFieldsToShow().isForeignBankSelected) {
            if (!ibanNumber?.trim()) {
                toast.error(t('PLEASE_ENTER_IBAN'));
                return;
            }
            if (!bicNumber?.trim()) {
                toast.error(t('PLEASE_ENTER_BIC'));
                return;
            }
        } else if (getFieldsToShow().isSwedishBankSelected) {
            if (!clearingNumber?.trim()) {
                toast.error(t('PLEASE_ENTER_CLEARING'));
                return;
            }
            if (!accountNumber?.trim()) {
                toast.error(t('PLEASE_ENTER_ACCOUNT'));
                return;
            }
            if (!/^\d{4,5}$/.test(clearingNumber)) {
                toast.error(t('INVALID_CLEARING_FORMAT'));
                return;
            }
            if (!/^\d{6,}$/.test(accountNumber)) {
                toast.error(t('INVALID_ACCOUNT_FORMAT'));
                return;
            }
        }
        if (user?.userId) {
            generateOTPForBank();
        }
        setIsBankEditMode(false);
    };

    const handleBankCancel = () => {
        setIsBankEditMode(false);
        setBankName(personalData?.bank || '');
        setCustomBankName(personalData?.bank || '');
        setClearingNumber(personalData?.clearingNumber || '');
        setAccountNumber(personalData?.accountNumber || '');
        setIbanNumber(personalData?.ibanNumber || '');
        setBicNumber(personalData?.bicNumber || '');
    };

    const handleRemoveBankAccount = () => {
        dispatch(removeBankAccount());
        setIsRemoveBankModalOpen(false);
        setBankName('');
        setClearingNumber('');
        setAccountNumber('');
    };

    const handleTaxEditDetails = () => {
        setOriginalTaxRate(taxRate);
        setIsTaxEditMode(true);
        setTaxRateOption(undefined);
      setTaxDisclaimerChecked(false);
        // Initialize tax rate option based on current rate
        const currentRate = Number(taxRate);
        // if (currentRate >= 10 && currentRate < 30) {
        //     setTaxRateOption('yes');
        // } else if (currentRate >= 30 && currentRate <= 55) {
        //     setTaxRateOption('no');
        // } else {
        //     setTaxRateOption('no'); // Default to 'no' if out of range
        // }
    };

    const handleTaxRequestChanges = () => {
        if (isGuestInvozioEmail(personalData.email)) {
            toast.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
            return
        }
        const taxPercentage = parseFloat(taxRate);
        const originalTaxPercentage = parseFloat(originalTaxRate);

        if (isNaN(taxPercentage) || taxPercentage < 0 || taxPercentage > 100) {
            toast.error('PLEASE_ENTER_VALID_TAX_RATE');
            return;
        }
                if (!taxAdjustmentFile && !taxDisclaimerChecked) {
                        toast.error(t('PLEASE_ACCEPT_TAX_DECLARATION') as string);
                        return;
                }
        dispatch(updateTaxInformation({
            taxPercentage: taxPercentage,
            file: taxAdjustmentFile || undefined,
            language: 'sv-se'
        })).then(() => {
            dispatch(fetchTaxInformation());
            setTaxAdjustmentFile(null); // Reset PDF selection after submit
        });

        setIsTaxEditMode(false);
        setOriginalTaxRate('');
    };

    const handleTaxCancel = () => {
        setIsTaxEditMode(false);
        if (taxInformation) {
            const currentTax = taxInformation.requestStatus?.toLowerCase() === 'pending'
                ? taxInformation.oldTaxValue
                : taxInformation.newTaxValue || taxInformation.oldTaxValue;
            setTaxRate(currentTax?.toString() || '');
        }
        setTaxRequest(taxInformation.requestStatus.toLowerCase());
        setTaxAdjustmentFile(null);
        setOriginalTaxRate('');
        setTaxRateOption('no'); // Reset radio button to default
    };
    
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setTaxAdjustmentFile(event.target.files[0]);
        }
    };

    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setLogoFile(event.target.files[0]);
        }
    };

    const handleDOBInputFocus = () => {
        setShowDOBCalendar(true);
    };

    const handleDOBSelect = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}${month}${day}000`;
        handleValueChange('dateOfBirth', formattedDate);
        setShowDOBCalendar(false);
    };

    const handleRemoveCurrency = (currency: string) => {
        if (Array.isArray(selectedCurrencies)) {
            setSelectedCurrencies(selectedCurrencies.filter(c => c !== currency));
        }
    };

    const handleAddCurrency = (currency: string) => {
        if (currency && Array.isArray(selectedCurrencies) && !selectedCurrencies.includes(currency)) {
            setSelectedCurrencies([...selectedCurrencies, currency]);
        } else if (currency && !Array.isArray(selectedCurrencies)) {
            setSelectedCurrencies([currency]);
        }
    };

    const handlePreferencesSave = () => {
        if (isGuestInvozioEmail(personalData.email)) {
            toast.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
            return;
        }
        // Required field validation for preferences
        if (!organizationName?.trim()) {
            toast.error(t('ORGANIZATION_NAME_REQUIRED'));
            return;
        }
        if (!Array.isArray(selectedCurrencies) || selectedCurrencies.length === 0) {
            toast.error(t('PLEASE_SELECT_AT_LEAST_ONE_CURRENCY') as string);
            return;
        }
        // If logo is required, add validation here (optional)
        // if (!logoFile) {
        //     toast.error(t('LOGO_REQUIRED'));
        //     return;
        // }
        dispatch(updateUserPreferences({
            organizationName: organizationName,
            logo: logoFile || undefined,
            currencies: selectedCurrencies
        })).then(() => {
            dispatch(fetchUserPreferences());
        });
    };

    const handlePreferencesCancel = () => {
        if (userPreferences) {
            setOrganizationName(userPreferences?.organizationName || '');
            setSelectedCurrencies(normalizeCurrencies(userPreferences?.currencies || []));
        } else {
            setOrganizationName('');
            setSelectedCurrencies([]);
        }
        setLogoFile(null);
    };

    const generateOTPForBank = async () => {
        if (isGuestInvozioEmail(personalData.email)) {
            toast.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
            return
        }
        setOtpLoading(true);
        try {
            const baseUrl = (import.meta as any).env.VITE_API_URL;
            const token = getItem('token');
            const language = getItem('i18nextLng') || 'en';

            const response = await apiCall.post(
                `${baseUrl}/settings/generate-otp`,
                {
                    type: 3,
                    email: '',
                    lang: language
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response?.status === 200) {
                setOtpCounter(300);
                setIsOTPModalOpen(true);
                setOTPField(new Array(4).fill(''));
                toast.success(t('OTP_SENT_TO_EMAIL') as string);
            }
        } catch (error: any) {
            toast.error((error?.response?.data?.message || 'Failed to generate OTP') as string);
        } finally {
            setOtpLoading(false);
        }
    };

    const handleCancelApplication = async () => {
        try {
            await dispatch(cancelTaxRequest() as any).unwrap();
            dispatch(fetchTaxInformation());
            setTaxRate(personalData.incomeTax);
        } catch (error: any) {
            console.error("Cancel tax request error:", error);
        }
    }


    const handleOTPChange = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (isNaN(Number(value))) return;

        const newOtp = [...OTPField];
        newOtp[index] = value.substring(value.length - 1);
        setOTPField(newOtp);

        if (value && index < 3 && inputRefs.current[index + 1]) {
            inputRefs.current[index + 1]?.focus();
        }
        if (value && index === 3 && newOtp.every(digit => digit)) {
            submitBankWithOTP(newOtp.join(''));
        }
    };

    const submitBankWithOTP = async (otp: string) => {
        setOtpLoading(true);
        try {
            const isForeignBankSelected = bankName?.toLowerCase().includes('foreign') ||
                bankName?.toLowerCase().includes('bic') ||
                bankName?.toLowerCase().includes('iban') ||
                bankName?.toLowerCase().includes('revolut');

            const bankDataPayload: any = {
                bank: getFieldsToShow().isOtherBankSelected ? customBankName : bankName,
                code: otp,
                clearingNumber: isForeignBankSelected ? '' : clearingNumber,
                accountNumber: isForeignBankSelected ? '' : accountNumber
            };

            if (isForeignBankSelected) {
                bankDataPayload.ibanNumber = ibanNumber;
                bankDataPayload.bicNumber = bicNumber;
            }

            await dispatch(updateBankData({
                userId: user.userId,
                bankData: bankDataPayload
            })).unwrap();

            setIsOTPModalOpen(false);
            setOTPField(new Array(4).fill(''));
            setOtpCounter(300);

            dispatch(fetchPersonalData(user.userId));
        } catch (error: any) {
            setIsOTPModalOpen(false);
            dispatch(fetchPersonalData(user.userId));
            console.error('OTP verification failed', error);
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendOTP = () => {
        setOTPField(new Array(4).fill(''));
        setOtpCounter(300);
        generateOTPForBank();
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const generateOTPForOldEmail = async () => {
        if (!emailToVerify || !validateEmail(emailToVerify)) {
            toast.error((t('ENTER_VALID_EMAIL') || 'Please enter a valid email address') as string);
            return;
        }

        if (emailToVerify === personalData?.email) {
            toast.error((t('NEW_EMAIL_SAME_AS_OLD') || 'New email cannot be the same as your current email') as string);
            return;
        }

        if (isGuestInvozioEmail(emailToVerify)) {
            toast.error((t('GUEST_EMAIL_NOT_ALLOWED') || 'Guest emails are not allowed. Please use your personal email address.') as string);
            return;
        }

        setEmailOtpLoading(true);
        try {
            const baseUrl = (import.meta as any).env.VITE_API_URL;
            const token = getItem('token');
            const language = getItem('i18nextLng') || 'en';

            // Check if current email is a guest Invozio email
            if (isGuestInvozioEmail(personalData?.email || '')) {
                // For guest emails, use the signup controller API
                const response = await apiCall.post(
                    `${baseUrl}/settings/generate-otp`,
                    {
                        type: 2,
                        email: emailToVerify,
                        lang: language
                    },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                if (response?.status === 200) {
                    setIsOldEmailVerified(true);
                    setEmailVerificationStep('verify-new-email');
                    setEmailOtpCounter(300);
                    setEmailOTPField(new Array(4).fill(''));
                    toast.success((t('OTP_SENT_TO_NEW_EMAIL') || 'OTP sent to your new email address') as string);
                }
            } else {
                // For regular emails, use the existing settings API
                const response = await apiCall.post(
                    `${baseUrl}/settings/generate-otp`,
                    {
                        type: 1,
                        lang: language
                    },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                if (response?.status === 200) {
                    setEmailVerificationStep('verify-old-email');
                    setEmailOtpCounter(300);
                    setEmailOTPField(new Array(4).fill(''));
                    toast.success((t('OTP_SENT_TO_EMAIL') || 'OTP sent to your current email') as string);
                }
            }
        } catch (error: any) {
            dispatch(resetMessages());
            toast.error(((error?.response?.data?.message)))
        } finally {
            setEmailOtpLoading(false);
        }
    };

    const handleEmailOTPChange = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (isNaN(Number(value))) return;

        const newOtp = [...emailOTPField];
        newOtp[index] = value.substring(value.length - 1);
        setEmailOTPField(newOtp);

        if (value && index < 3 && emailInputRefs.current[index + 1]) {
            emailInputRefs.current[index + 1]?.focus();
        }

        if (value && index === 3 && newOtp.every(digit => digit)) {
            if (emailVerificationStep === 'verify-old-email') {
                verifyOldEmailOTP(newOtp.join(''));
            } else if (emailVerificationStep === 'verify-new-email') {
                verifyNewEmailOTP(newOtp.join(''));
            }
        }
    };

    const verifyOldEmailOTP = async (otp: string) => {
        setEmailOtpLoading(true);
        try {
            const baseUrl = (import.meta as any).env.VITE_API_URL;
            const token = getItem('token');

            const verifyOldResponse = await apiCall.put(
                `${baseUrl}/employee/verify-old?code=${otp}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (verifyOldResponse?.status === 200) {
                toast.success((t('OLD_EMAIL_VERIFIED_SUCCESSFULLY') || 'Old email verified successfully') as string);
                setIsOldEmailVerified(true);
                setEmailVerificationStep('verify-new-email');
                setEmailOTPField(new Array(4).fill(''));
                setEmailOtpCounter(300);
                generateOTPForNewEmail();
            }
        } catch (error: any) {
            dispatch(resetMessages());
            toast.error(t(error?.response?.data?.message) as string);
        } finally {
            setEmailOtpLoading(false);
        }
    };

    const generateOTPForNewEmail = async () => {
        setEmailOtpLoading(true);
        try {
            const baseUrl = (import.meta as any).env.VITE_API_URL;
            const token = getItem('token');
            const language = getItem('i18nextLng') || 'en';
            const response = await apiCall.post(
                `${baseUrl}/settings/generate-otp`,
                {
                    type: 2,
                    email: emailToVerify,
                    lang: language
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response?.status === 200) {
                setEmailVerificationStep('verify-new-email');
                setEmailOtpCounter(300);
                setEmailOTPField(new Array(4).fill(''));
                toast.success((t('OTP_SENT_TO_NEW_EMAIL') || 'OTP sent to your new email address') as string);
            }
        } catch (error: any) {
            toast.error(t(error?.response?.data?.message) as string);
        } finally {
            setEmailOtpLoading(false);
        }
    };

    const verifyNewEmailOTP = async (otp: string) => {
        setEmailOtpLoading(true);
        try {
            const baseUrl = (import.meta as any).env.VITE_API_URL;
            const token = getItem('token');

            // Verify new email and update it
            const verifyNewResponse = await apiCall.put(
                `${baseUrl}/signupuserv2/verify-new?code=${otp}&email=${emailToVerify}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (verifyNewResponse?.status === 200) {
                toast.success((t('EMAIL_CHANGED_SUCCESSFULLY') || 'Email changed successfully') as string);
                setIsEmailVerificationModalOpen(false);
                setEmailToVerify('');
                setEmailOTPField(new Array(4).fill(''));
                setEmailOtpCounter(300);
                setEmailVerificationStep('enter-email');
                setIsOldEmailVerified(false);
                dispatch(fetchPersonalData(user.userId));
            }
        } catch (error: any) {
            toast.error(t(error?.response?.data?.message || 'Email verification failed') as string);
        } finally {
            setEmailOtpLoading(false);
        }
    };

    const handleEmailResendOTP = () => {
        setEmailOTPField(new Array(4).fill(''));
        setEmailOtpCounter(300);

        if (emailVerificationStep === 'verify-old-email') {
            generateOTPForOldEmail();
        } else if (emailVerificationStep === 'verify-new-email') {
            generateOTPForNewEmail();
        }
    };

    const handleOpenEmailVerification = () => {
        setIsEmailVerificationModalOpen(true);
        setEmailToVerify('');
        setEmailOTPField(new Array(4).fill(''));
        setEmailOtpCounter(300);

        // For guest emails, show email input step first
        if (isGuestInvozioEmail(personalData?.email || '')) {
            setEmailVerificationStep('enter-email');
            setIsOldEmailVerified(true);
        } else {
            setEmailVerificationStep('enter-email');
            setIsOldEmailVerified(false);
        }
    };

    const handleCloseEmailVerification = () => {
        setIsEmailVerificationModalOpen(false);
        setEmailToVerify('');
        setEmailOTPField(new Array(4).fill(''));
        setEmailOtpCounter(300);
        setEmailVerificationStep('enter-email');
        setIsOldEmailVerified(false);
    };

    // OTP Counter Timer
    useEffect(() => {
        let intervalId: NodeJS.Timeout;
        if (otpCounter > 0 && isOTPModalOpen) {
            intervalId = setInterval(() => {
                setOtpCounter(prev => (prev > 0 ? prev - 1 : 0));
            }, 1000);
        }
        return () => clearInterval(intervalId);
    }, [otpCounter, isOTPModalOpen]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout;
        if (emailOtpCounter > 0 && isEmailOTPModalOpen) {
            intervalId = setInterval(() => {
                setEmailOtpCounter(prev => (prev > 0 ? prev - 1 : 0));
            }, 1000);
        }
        return () => clearInterval(intervalId);
    }, [emailOtpCounter, isEmailOTPModalOpen]);

    const tabs = [
        { key: 'profile' as TabType, label: t('PROFILE_SETTINGS') || 'Profile Settings' },
        { key: 'bank' as TabType, label: t('BANK_INFORMATION') || 'Bank Information' },
        { key: 'preferences' as TabType, label: t('PREFERENCES') || 'Preferences' },
    ];

    useEffect(() => {
        if (user?.userId) {
            dispatch(fetchPersonalData(user.userId));
            dispatch(fetchTaxInformation())
            dispatch(fetchUserPreferences());
        }
        return () => {
            dispatch(resetMessages());
        };
    }, [dispatch, user?.userId]);
    function getTaxStatusLabel(status: string | undefined): string {
        if (!status) return '';
        switch (status.toLowerCase()) {
            case 'pending':
                return t('REQUEST_SENT') || 'Request Sent';
            case 'notapproved':
                return t('DECLINED') || 'Declined';
            case 'completed':
                return t('APPROVED') || '';
            default:
                return status;
        }
    }

    useEffect(() => {
        if (isEditMode && personalData) {
            setFormData(personalData);
        }
    }, [isEditMode, personalData]);

    function handleRemovePopupModalOpen() {
        if (isGuestInvozioEmail(personalData.email)) {
            toast.error(t('PLEASE_VERIFY_YOUR_EMAIL') as string)
            return
        }
        setIsRemoveBankModalOpen(true)
    }

    function handleCloseOtpModal() {
        setIsOTPModalOpen(false);
        setOTPField(new Array(4).fill(''));
        setOtpCounter(300);
        setBankName(personalData.bank || '');
        setCustomBankName(personalData.bank || '');
        setClearingNumber(personalData.clearingNumber || '');
        setAccountNumber(personalData.accountNumber || '');
        setIbanNumber(personalData.ibanNumber || '');
        setBicNumber(personalData.bicNumber || '');
        setTaxRate(personalData.incomeTax || '30');
    }
    const isTaxValid = () => {
        const numericValue = Number(taxRate);
        // Must not be empty, not zero, and acknowledgment checked
        if (!taxRate || numericValue === 0 || !taxDisclaimerChecked) return false;
        if (taxRateOption === "yes") {
            // Main Employer: tax must be between 10 and 55, acknowledgment checked
            return numericValue >= 1 && numericValue <= 55 && taxDisclaimerChecked;
        }
        if (taxRateOption === "no") {
            // Part Time: tax must be between 30 and 55, acknowledgment checked
            return numericValue >= 1 && numericValue <= 55 && taxDisclaimerChecked;
        }
        return false;
    };

    return (
        <div className="dashboard-layout">
            <Sidebar
                activeItemId="settings"
                footer={null}
                onClose={() => setIsSidebarOpen(false)}
                className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
            />
            {isSidebarOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}
            <div className="dashboard-content">
                <Header />
                <div className="profile-settings-page">
                    <div className="profile-settings-header">
                        <button className="back-button" onClick={handleBack}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <h1 className="page-title">{t('ACCOUNT_SETTINGS')}</h1>
                    </div>

                    <div className="profile-settings-content px-0 d-flex flex-column flex-sm-row">
                        <Col md={3} className="profile-tabs-sidebar ">
                            <div className="tabs-container flex-row flex-sm-column ">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.key}
                                        className={`tab-item  ${activeTab === tab.key ? 'active' : ''}`}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        <span className="tab-label">{tab.label}</span>
                                        {activeTab === tab.key && (
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <path
                                                    d="M7.5 15L12.5 10L7.5 5"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </Col>

                        <Col md={9} className="profile-content-area">
                            {isLoading ? (
                                <div className="loading-spinner mx-auto d-flex  my-5">
                                    <div className="spinner"></div>
                                </div>
                            ) : (
                                <>
                                    {activeTab.includes('profile') && (
                                        <>
                                            <div className="profile-card my-sm-0 my-3 ">
                                                <div className="card-header">
                                                    <h2 className="card-title">{t('YOUR_PROFILE') || 'Your Profile'}</h2>
                                                    {!isEditMode && (
                                                        <button className="edit-profile-btn" onClick={handleEditProfile}>
                                                            {t('EDIT_PROFILE')}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="card-body">
                                                    {/* User Avatar & Name */}
                                                    <div className="user-profile-section">
                                                        <div className="user-avatar-wrapper">
                                                            <div className={`user-avatar ${isEditMode ? '' : ''}`}>
                                                                {personalData?.profileImage ? (
                                                                    <img src={personalData.profileImage} alt={personalData.fullName} />
                                                                ) : (
                                                                    <div className="avatar-placeholder">
                                                                        {(personalData?.firstName?.charAt(0) + personalData?.lastName?.charAt(0)) || ''}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="user-info">
                                                            <h3 className="user-name">{personalData?.firstName} {personalData?.lastName}</h3>
                                                        </div>
                                                    </div>

                                                    {!isEditMode ? (
                                                        <div className="profile-details">
                                                            <div className="details-row">
                                                                <div className="detail-item  d-flex flex-row flex-sm-column justify-content-sm-between gap-3">
                                                                    <label className='detail-item-label-full-name'>{t('FIRST_NAME') || 'First Name'}</label>
                                                                    <p className='detail-item-p-full-name'>{personalData?.firstName || ''}</p>
                                                                </div>
                                                                   <div className="detail-item  d-flex flex-row flex-sm-column justify-content-sm-between gap-3">
                                                                    <label className='detail-item-label-full-name'>{t('LAST_NAME') || 'First Name'}</label>
                                                                    <p className='detail-item-p-full-name'>{personalData?.lastName || ''}</p>
                                                                </div>
                                                                <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                    <label>{t('EMAIL_ID') || 'Email ID'}</label>
                                                                    <div className="email-verified" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            <p style={{ margin: 0 }}>{isGuestInvozioEmail(personalData?.email || '') ? 'N/A' : personalData?.email}</p>
                                                                            {personalData?.isVerified && (
                                                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                                    <circle cx="8" cy="8" r="7" stroke="#27B973" strokeWidth="1.5" />
                                                                                    <path
                                                                                        d="M5.5 8L7 9.5L10.5 6"
                                                                                        stroke="#27B973"
                                                                                        strokeWidth="1.5"
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                    />
                                                                                </svg>
                                                                            )}
                                                                        </div>
                                                                        {(isEditMode || isGuestInvozioEmail(personalData?.email || '')) && (
                                                                            <button className="btn-verify px-3 py-2" onClick={handleOpenEmailVerification} >
                                                                                {isGuestInvozioEmail(personalData?.email || '') ? (t('VERIFY_EMAIL') || 'Verify Email') : (t('EDIT_EMAIL') || 'Edit Email')}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="details-row">
                                                             <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                    <label>{t('MOBILE_NUMBER') || ''}</label>
                                                                    <p>{personalData?.phoneNo || 'N/A'}</p>
                                                                </div>
                                                                {personalData?.ssn ? (
                                                                    <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                        <label>{t('SOCIAL_SECURITY_NUMBER') || 'Social Security Number'}</label>
                                                                        <p>{personalData?.ssn || 'N/A'}</p>
                                                                    </div>
                                                                ) :
                                                                    (
                                                                        <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                            <label>{t('DATE_OF_BIRTH_FOR_PROFILE') || 'Date of Birth'}</label>
                                                                            <p>{personalData?.dateOfBirth || 'N/A'}</p>
                                                                        </div>
                                                                    )

                                                                }
                                                                <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                    <label>{t('PROFESSION') || 'Profession'}</label>
                                                                    <p>{personalData?.profession || 'N/A'}</p>
                                                                </div>
                                                            </div>
                                                            <div className="details-row">                                                                <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                    <label>{t('COUNTRY') || 'Country'}</label>
                                                                    <p>{personalData?.country ? t(getCountryTranslationKey(personalData.country)) || personalData.country : 'N/A'}</p>
                                                                </div>
                                                                <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                    <label>{t('CITY') || 'City'}</label>
                                                                    <p>{personalData?.city || 'N/A'}</p>
                                                                </div>
                                                                <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                    <label>{t('ADDRESS') || 'Address'}</label>
                                                                    <p>{personalData?.address || 'N/A'}</p>
                                                                </div>
                                                                <div className="detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                    <label>{t('POSTAL_CODE') || 'Postal Code'}</label>
                                                                    <p>{personalData?.zipCode || 'N/A'}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="profile-details">
                                                            <div className="details-row">
                                                                <div className="detail-item">
                                                                    <label>{t('FIRST_NAME') || 'Full Name'}</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        value={formData.firstName ?? personalData?.firstName ?? ''}
                                                                        onChange={(e) => handleValueChange('firstName', e.target.value)}
                                                                        placeholder="Enter full name"
                                                                    />
                                                                </div>
                                                                    <div className="detail-item">
                                                                    <label>{t('LAST_NAME') || 'Last Name'}</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        value={formData.lastName ?? personalData?.lastName ?? ''}
                                                                        onChange={(e) => handleValueChange('lastName', e.target.value)}
                                                                        placeholder="Enter Last name"
                                                                    />
                                                                </div>
                                                                <div className="detail-item">
                                                                    <label className="text-muted">{t('EMAIL_ID') || 'Email ID'}</label>
                                                                    <div className="input-button-group">
                                                                        <div className="input-with-icon disabled">
                                                                            {personalData?.isVerified && (
                                                                                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" className="check-icon">
                                                                                    <circle cx="8.5" cy="8.5" r="7.75" stroke="#27B973" strokeWidth="1.5" />
                                                                                    <path
                                                                                        d="M5.5 8.5L7.5 10.5L11.5 6.5"
                                                                                        stroke="#27B973"
                                                                                        strokeWidth="1.5"
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                    />
                                                                                </svg>
                                                                            )}
                                                                            <input
                                                                                type="email"
                                                                                className="form-input"
                                                                                value={isGuestInvozioEmail(personalData?.email || '') ? '' : personalData?.email}
                                                                                disabled
                                                                                readOnly
                                                                            />
                                                                        </div>
                                                                        <button className="btn-verify" onClick={handleOpenEmailVerification}>
                                                                            {t('EDIT_EMAIL') || 'Edit Email'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="details-row">

                                                                        {personalData?.ssn ? (
                                                                            <div className="detail-item">
                                                                                <label>{t('SOCIAL_SECURITY_NUMBER') || 'Social Security Number'}</label>
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-input"
                                                                                    value={formData.ssn ?? personalData?.ssn ?? ''}
                                                                                    onChange={(e) => {
                                                                                        const digits = e.target.value.replace(/\D/g, '');
                                                                                        handleValueChange('ssn', digits);
                                                                                    }}
                                                                                    maxLength={12}

                                                                                    placeholder="Enter SSN"
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                    <>
                                                                        <div className="detail-item">
                                                                            <label>{t('DATE_OF_BIRTH_FOR_PROFILE') || 'Date of Birth'}</label>
                                                                            <div className="input" style={{ position: 'relative' }}>
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-input"
                                                                                    value={formData.dateOfBirth ?? personalData?.dateOfBirth ?? ''}
                                                                                    onChange={(e) => handleValueChange('dateOfBirth', e.target.value)}
                                                                                    onFocus={handleDOBInputFocus}
                                                                                    placeholder="YYYYMMDD000"
                                                                                />
                                                                                <svg
                                                                                    onClick={handleDOBInputFocus}
                                                                                    style={{ cursor: 'pointer', position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    width={17}
                                                                                    height={19}
                                                                                    viewBox="0 0 17 19"
                                                                                    fill="none"
                                                                                >
                                                                                    <path
                                                                                        d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z"
                                                                                        stroke="black"
                                                                                        strokeWidth={2}
                                                                                        strokeLinecap="round"
                                                                                    />
                                                                                </svg>
                                                                                <div className="dob-calendar-wrapper" ref={dropdownRef}>
                                                                                    {showDOBCalendar && (
                                                                                        <div className="calendar-modal-overlay">
                                                                                            <div className="calendar-modal-content">
                                                                                                <CalendarSingle
                                                                                                    onDateSelect={handleDOBSelect}
                                                                                                    label="Date of Birth"
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <div className="detail-item">
                                                                    <label>{t('PROFESSION') || 'Profession'}</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        value={formData.profession ?? personalData?.profession ?? ''}
                                                                        onChange={(e) => handleValueChange('profession', e.target.value)}
                                                                        placeholder="Enter profession"
                                                                    />
                                                                </div>
                                                                <div className="detail-item">
                                                                    <label>{t('COUNTRY') || 'Country'}</label>
                                                                    <div className="select-wrapper">
                                                                        <select
                                                                            className="form-input"
                                                                            value={formData.country ?? personalData?.country ?? ''}
                                                                            onChange={(e) => handleSelectChange('country', e.target.value)}
                                                                        >
                                                                            <option value="">{t("SELECT_COUNTRY")}</option>
                                                                            {renderCountryOptionsWithI18n(t)}
                                                                        </select>
                                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dropdown-icon">
                                                                            <path
                                                                                d="M5 7.5L10 12.5L15 7.5"
                                                                                stroke="currentColor"
                                                                                strokeWidth="1.5"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            />
                                                                        </svg>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                                    <div className="details-row">
                                                                        <div className="detail-item">
                                                                            <label>{t('MOBILE_NUMBER') || 'Mobile Number'}</label>
                                                                            <div className="input-button-group">
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-input"
                                                                                    value={formData.phoneNo ?? personalData?.phoneNo ?? ''}
                                                                                    onChange={(e) => {
                                                                                        const digits = e.target.value.replace(/\D/g, '');
                                                                                        handleValueChange('phoneNo', digits);
                                                                                    }}
                                                                                    placeholder="Enter Mobile number"
                                                                                    maxLength={12}
                                                                                />
                                                                                {/* <button className="btn-verify">
                                                                            {t('VERIFY') || 'Verify'}
                                                                        </button> */}
                                                                            </div>
                                                                        </div>
                                                                <div className="detail-item">
                                                                    <label>{t('CITY') || 'City'}</label>
                                                                    <div className="select-wrapper">
                                                                        <input
                                                                            className="form-input"
                                                                            placeholder='Enter city Name'
                                                                            value={formData.city ?? personalData?.city ?? ''}
                                                                            onChange={(e) => handleSelectChange('city', e.target.value)}
                                                                        >
                                                                        </input>
                                                                        {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="dropdown-icon">
                                                                            <path
                                                                                d="M5 7.5L10 12.5L15 7.5"
                                                                                stroke="currentColor"
                                                                                strokeWidth="1.5"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            />
                                                                        </svg> */}
                                                                    </div>
                                                                </div>
                                                                <div className="detail-item">
                                                                    <label>{t('ADDRESS') || 'Address'}</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        value={formData.address ?? personalData?.address ?? ''}
                                                                        onChange={(e) => handleValueChange('address', e.target.value)}
                                                                        placeholder="Enter address"
                                                                    />
                                                                </div>
                                                                <div className="detail-item">
                                                                    <label>{t('POSTAL_CODE') || 'Postal Code'}</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        value={formData.zipCode ?? personalData?.zipCode ?? ''}
                                                                        onChange={(e) => handleValueChange('zipCode', e.target.value)}
                                                                        placeholder="Enter postal code"
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Form Actions */}
                                                            <div className="form-actions">
                                                                <button className="save-changes-btn" onClick={handleSaveChanges}>
                                                                    {t('SAVE_CHANGES') || 'Save Changes'}
                                                                </button>
                                                                <button className="Cancel-changes-btn" onClick={handleCancelEdit}>
                                                                    {t('CANCEL') || 'Cancel'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {activeTab.includes('bank') && (
                                        <div className="bank-tax-container my-sm-0 my-3">
                                            <div className="profile-card bank-card">
                                                <div className="card-header">
                                                    <h2 className="card-title">{t('BANKING_INFORMATION') || 'Banking Information'}</h2>
                                                </div>
                                                <div className="card-body">
                                                    {!isBankEditMode ? (
                                                        <>
                                                            <div className="bank-info-section">
                                                                <div className="bank-account-row">
                                                                    <div className="bank-detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                        <label className="detail-label">{t('BANK_NAME') || 'Bank Name'}</label>
                                                                        <p className="detail-value">{bankName || 'N/A'}</p>
                                                                    </div>

                                                                    {getFieldsToShow().isOtherBankSelected && (
                                                                        <div className="bank-detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                            <label className="detail-label">{t('BANK_NAME') || 'Bank Name'}</label>
                                                                            <p className="detail-value">{customBankName || 'Not provided'}</p>
                                                                        </div>
                                                                    )}

                                                                    {getFieldsToShow().isSwedishBankSelected && (
                                                                        <>
                                                                            <div className="bank-detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                                <label className="detail-label">{t('ACCOUNT_NUMBER') || 'Account Number'}</label>
                                                                                <p className="detail-value">{accountNumber || 'Not provided'}</p>
                                                                            </div>

                                                                            <div className="bank-detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                                <label className="detail-label">{t('CLEARING_NUMBER') || 'Clearing Number'}</label>
                                                                                <p className="detail-value">{clearingNumber || 'Not provided'}</p>
                                                                            </div>
                                                                        </>
                                                                    )}

                                                                    {getFieldsToShow().isForeignBankSelected && (
                                                                        <>
                                                                            <div className="bank-detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                                <label className="detail-label">{t('IBAN_NUMBER') || 'IBAN Number'}</label>
                                                                                <p className="detail-value">{ibanNumber || 'Not provided'}</p>
                                                                            </div>

                                                                            <div className="bank-detail-item detail-item  d-flex flex-row flex-sm-column justify-content-between">
                                                                                <label className="detail-label">{t('BIC_NUMBER') || 'BIC Number'}</label>
                                                                                <p className="detail-value">{bicNumber || 'Not provided'}</p>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                    {personalData.bankAccounts.length > 0 && (
                                                                        <div className="bank-actions">
                                                                            <button className="btn-outline-primary-preferences" onClick={handleBankEditDetails}>
                                                                                {t('EDIT_DETAILS') || 'Edit Details'}
                                                                            </button>
                                                                            <button className="btn-outline-primary-preferences" onClick={() => handleRemovePopupModalOpen()}>
                                                                                {t('REMOVE_ACCOUNT') || 'Remove Account'}
                                                                            </button>

                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {personalData.bankAccounts.length === 0 && (
                                                                    <button className="btn-primary-add-account" onClick={() => handleBankEditDetails()}>
                                                                        {t('ADD_NEW_ACCOUNT')}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {/* Revolut Information */}
                                                            <div className="revolut-info">
                                                                <h3 className="revolut-title">{t('GET_PAID_WITHIN_HOUR_WITH_REVOLUT') || 'Get paid within an hour with Revolut.'}</h3>
                                                                <p className="revolut-description">
                                                                    {t('REVOLUT_ACCOUNT_DESCRIPTION') || 'If you already have a Revolut account, simply update your bank details in the platform and we\'ll pay your salary into your Revolut account. Don\'t have one?'}
                                                                    {' '}
                                                                    <a href="https://revolut.com/referral/?referral-code=kevindtxd!JUN1-25-AR-H2&geo-redirect" target="_blank" rel="noopener noreferrer" className="revolut-link">
                                                                        {t('SIGN_UP_HERE')}
                                                                    </a>
                                                                    {' '}
                                                                    {t('AND_START_BENEFITING_TODAY')}
                                                                </p>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="bank-edit-section">
                                                                <div className="bank-edit-inputs">
                                                                    <div className="form-group">
                                                                        <label className="form-label">{t('BANK_NAME') || 'Bank Name'}</label>
                                                                        <select
                                                                            className="form-select"
                                                                            value={bankName}
                                                                            onChange={(e) => setBankName(e.target.value)}
                                                                        >
                                                                            <option value=""> {t("SELECT_ANY_BANK")} </option>
                                                                            <option value="Handelsbanken"> {t("HANDELSBANKEN")} </option>
                                                                            <option value="Nordea social security number">
                                                                                {" "}
                                                                                {t("NORDIA_SOCIAL_SECURITY_NUMBER")}
                                                                            </option>
                                                                            <option value="Nordea Account number">
                                                                                {" "}
                                                                                {t("NORDEA_ACCOUNT_NUMBER")}{" "}
                                                                            </option>
                                                                            <option value="Revolut Bank"> {t("REVOLUT_BANK")} </option>
                                                                            <option value="Swedbank"> {t("SWEDBANK")} </option>
                                                                            <option value="SEB"> {t("SEB")} </option>
                                                                            <option value="Foreign bank BIC / IBAN">
                                                                                {" "}
                                                                                {t("FOREIGN_BANK_BIC_IBAN")}{" "}
                                                                            </option>
                                                                            <option value="Other Bank">
                                                                                {" "}
                                                                                {t("OTHER_BANK")}{" "}
                                                                            </option>
                                                                            {![
                                                                                "Handelsbanken",
                                                                                "Nordea social security number",
                                                                                "Nordea Account number",
                                                                                "Revolut Bank",
                                                                                "Swedbank",
                                                                                "SEB",
                                                                                "Foreign bank BIC / IBAN"
                                                                            ].includes(bankName) && (
                                                                                    <option value={bankName}>{bankName}</option>
                                                                                )}
                                                                        </select>
                                                                    </div>

                                                                    {/* Other Bank - Custom Bank Name */}
                                                                    {getFieldsToShow().isOtherBankSelected && (
                                                                        <div className="form-group">
                                                                            <label className="form-label">{t('BANK_NAME_CUSTOM') || 'Enter Bank Name'}</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-input"
                                                                                value={customBankName}
                                                                                onChange={(e) => setCustomBankName(e.target.value)}
                                                                                placeholder={t('TYPE_HERE') || 'Type here....'}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {/* Swedish Bank Fields - Clearing and Account Numbers */}
                                                                    {(!["Foreign bank BIC / IBAN", "Revolut Bank"].includes(bankName)) && (
                                                                        <>
                                                                            <div className="form-group">
                                                                                <label className="form-label">{t('CLEARING_NUMBER') || 'Clearing Number'}</label>
                                                                                <input
                                                                                    type="text"
                                                                                    pattern="[0-9]*"
                                                                                    inputMode="numeric"
                                                                                    maxLength={5}
                                                                                    className="form-input"
                                                                                    value={clearingNumber}
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value.replace(/\D/g, ''); // only digits
                                                                                        if (value.length <= 5) setClearingNumber(value);
                                                                                    }}
                                                                                    placeholder={t('TYPE_HERE') || 'Type here....'}
                                                                                />
                                                                            </div>

                                                                            <div className="form-group">
                                                                                <label className="form-label">{t('ACCOUNT_NUMBER') || 'Account Number'}</label>
                                                                                <input
                                                                                    type="text"
                                                                                    pattern="[0-9]*"
                                                                                    inputMode="numeric"
                                                                                    className="form-input"
                                                                                    value={accountNumber}
                                                                                    maxLength={10}
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value.replace(/\D/g, ''); // only digits
                                                                                        if (value.length <= 10) setAccountNumber(value);
                                                                                    }}
                                                                                    placeholder={t('TYPE_HERE') || 'Type here....'}
                                                                                />
                                                                            </div>
                                                                        </>
                                                                    )}

                                                                    {/* Foreign Bank Fields - BIC and IBAN Numbers */}
                                                                    {getFieldsToShow().isForeignBankSelected && (
                                                                        <>
                                                                            <div className="form-group">
                                                                                <label className="form-label">{t('BIC_NUMBER') || 'BIC Number'}</label>
                                                                                <input
                                                                                    type="text"
                                                                                    pattern="[0-9]*"
                                                                                    inputMode="numeric"
                                                                                    // maxLength={5}
                                                                                    className="form-input"
                                                                                    value={bicNumber}
                                                                                    onChange={(e) => {
                                                                                        // const value = e.target.value.replace(/\D/g, ''); // only digits
                                                                                        setBicNumber(e.target.value);
                                                                                    }}
                                                                                    placeholder={t('TYPE_HERE') || 'Type here....'}
                                                                                />
                                                                            </div>

                                                                            <div className="form-group">
                                                                                <label className="form-label">{t('IBAN_NUMBER') || 'IBAN Number'}</label>
                                                                                <input
                                                                                    type="text"
                                                                                    pattern="[0-9]*"
                                                                                    inputMode="numeric"
                                                                                    className="form-input"
                                                                                    value={ibanNumber}
                                                                                    // maxLength={10}
                                                                                    onChange={(e) => {
                                                                                        // const value = e.target.value.replace(/\D/g, ''); // only digits
                                                                                        setIbanNumber(e.target.value);
                                                                                    }}
                                                                                    placeholder={t('TYPE_HERE')}
                                                                                />
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>

                                                                {/* Revolut Information */}
                                                                <div className="revolut-info">
                                                                    <h3 className="revolut-title">{t('GET_PAID_REVOLUT') || 'Get paid within an hour with Revolut.'}</h3>
                                                                    <p className="revolut-description">
                                                                        {t('REVOLUT_ACCOUNT_DESCRIPTION')}
                                                                        {' '}
                                                                        <a href="https://revolut.com/referral/?referral-code=kevindtxd!JUN1-25-AR-H2&geo-redirect" target="_blank" rel="noopener noreferrer" className="revolut-link">
                                                                            {t('SIGN_UP_HERE')}
                                                                        </a>
                                                                        {' '}
                                                                        {t('AND_START_BENEFITING_TODAY')}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {isBankEditMode && (
                                                    <div className="card-footer">
                                                        <div className="form-actions">
                                                            <button className="save-changes-btn" onClick={handleBankSaveChanges}>
                                                                {t('SAVE_CHANGES')}
                                                            </button>
                                                            <button className="Cancel-changes-btn" onClick={handleBankCancel}>
                                                                {t('CANCEL')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Income Tax Card */}
                                            <div className="profile-card tax-card">
                          <div className="card-header">
                            <h2 className="card-title">{t("INCOME_TAX")}</h2>
                          </div>
                          <div className="card-body">
                            {!isTaxEditMode ? (
                              <>
                                <div className="tax-info-section">
                                  {/* Tax Rate Details - View Mode */}
                                  <div className="tax-rate-row">
                                    <div className="tax-rate-content">
                                      <div className="tax-detail-item">
                                        <label className="detail-label">{t("Income Tax")}</label>
                                                                                <p className="detail-value tax-percentage">{taxRate}%</p>
                                                                                {taxInformation?.requestStatus?.toLowerCase() === 'pending' && taxInformation?.oldTaxValue !== undefined && (
                                                                                    <p className="detail-value" style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px' }}>
                                                                                        {t('OLD_TAX_VALUE')}: {taxInformation.oldTaxValue}%
                                                                                    </p>
                                                                                )}
                                                                                { }
                                                                                {taxInformation?.requestStatus?.toLowerCase() === 'completed' && taxInformation?.newTaxValue !== undefined && taxInformation?.newTaxValue !== taxInformation?.oldTaxValue && (
                                                                                    <p className="detail-value" style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px' }}>
                                                                                        {t('NEW_TAX_VALUE')}: {taxInformation.newTaxValue}%
                                                                                    </p>
                                                                                )}

                                      </div>
                                      {taxInformation?.requestStatus && <span className={`status-badge ${taxInformation?.requestStatus?.toLowerCase() === "pending" ? "status-request-sent" : taxInformation?.requestStatus?.toLowerCase() === "completed" ? "status-approved" : "status-rejected"}`}>{getTaxStatusLabel(taxInformation?.requestStatus)}</span>}
                                    </div>
                                    {!taxInformation?.requestStatus ||
                                      (taxInformation?.requestStatus?.toLowerCase() === "completed" && (
                                        <div className="tax-actions">
                                          <button className="edit-details-btn" onClick={handleTaxEditDetails}>
                                            {t("EDIT_DETAILS")}
                                          </button>
                                        </div>
                                      ))}
                                  </div>

                                                                    {taxInformation?.requestStatus?.toLowerCase() === "pending" && (
                                                                        <div className="tax-actions">
                                                                            <button className="cancel-application-btn" onClick={handleCancelApplication}>
                                                                                {t("CANCEL_APPLICATION", { lng: 'en' })}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {/* Change Tax Rate Information */}
                                                                <div className="tax-change-info">
                                                                    <h3 className="tax-change-title">{t("CHANGE_YOUR_INCOME_TAX_RATE")}</h3>
                                                                    <p className="tax-change-description">{t("TAX_CHANGE_DESCRIPTION")}</p>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                {/* Tax Edit Section */}
                                <div className="tax-edit-section">
                                  {/* Radio Button Selection - Yes/No */}
                                  <div className="form-group">
                                    <div className="radio-buttons-group">
                                     <span className="radio-label" style={{fontSize: '16px'}}>{t("EMPLOYER_TYPE")}</span>
                                     <div className='radio-buttons-row'>
                                      <div className="radio-option" style={{fontSize: '14px'}}>
                                        <input
                                          type="radio"
                                          id="tax-option-yes"
                                          name="tax-option"
                                          value="yes"
                                          checked={taxRateOption === "yes"}
                                          onChange={() => {
                                            setTaxRateOption("yes");
                                            // Reset tax rate if it's out of range for yes option
                                            const currentValue = Number(taxRate);
                                            if (currentValue < 10 || currentValue > 55) {
                                              setTaxRate("");
                                            }
                                          }}
                                        />
                                        <label htmlFor="tax-option-yes">{t("Main_Employer") || "Yes"} </label>
                                      </div>
                                      <div className="radio-option">
                                        <input
                                          type="radio"
                                          id="tax-option-no"
                                          name="tax-option"
                                          value="no"
                                          checked={taxRateOption === "no"}
                                          onChange={() => {
                                            setTaxRateOption("no");
                                            const currentValue = Number(taxRate);
                                            if (currentValue < 30 || currentValue > 55) {
                                              setTaxRate("");
                                            }
                                          }}
                                        />
                                        <label htmlFor="tax-option-no">{t("Part_time") || "No"}</label>
                                      </div>
                                    </div>
                                    </div>
                                  </div>

                                  {/* Income Tax Input with % */}
                                  {taxRateOption !== undefined && (
                                    <div className="form-group">
                                      <label className="form-label">{t("Income Tax")}</label>
                                      
                                      <div className="input-with-suffix">
                                        <input
                                          type="text"
                                          pattern="[0-9]*"
                                          inputMode="numeric"
                                          className={`form-input ${taxError ? "input-error" : ""}`}
                                          value={taxRate}
                                          onChange={(e) => {
                                            const rawValue = e.target.value.replace(/\D/g, "");
                                            const numericValue = Number(rawValue);
                                            if (numericValue > 55) return;
                                            setTaxRate(rawValue);
                                            if (rawValue === "") {
                                                setTaxError("");
                                                return;
                                            }
                                            if (taxRateOption === "yes") {
                                                if (numericValue < 10 || numericValue > 55) {
                                                // setTaxError("Tax should be between 10 to 55");
                                                } else {
                                                setTaxError("");
                                                }
                                            }
                                            if (taxRateOption === "no") {
                                                if (numericValue < 30 || numericValue > 55) {
                                                // setTaxError("Tax should be between 30 to 55");
                                                } else {
                                                setTaxError("");
                                                }
                                            }
                                            }}
                                          placeholder={t("Type here....") || "Type here...."}
                                        />
                                        <span className="input-suffix">%</span>
                                      </div>
                                      {taxError && (
                                        <div className="tax-warning">
                                            {taxError}
                                        </div>
                                        )}  
                                                                            {taxRateOption === "yes" && (
                                                                                <div className="tax-help-line" style={{ marginTop: '8px' }}>
                                                                                    <span className="tax-help-text" style={{ fontSize: '14px' }}>
                                                                                        {t("HOW_TO_CALCULATE_TAX") || "If you don't know how to calculate tax then"} <a href="https://www7.skatteverket.se/portal/inkomst-efter-skatt-se-tabell" target="_blank" rel="noopener noreferrer">{t("CLICK_HERE") || 'click here'}</a>.
                                                                                    </span>
                                                                                </div>
                                                                            )}

                                                                            {/* PDF Upload if tax is below 10 and not 0 */}
                                                                            {taxRate && !isNaN(Number(taxRate)) && Number(taxRate) < 10 && Number(taxRate) !== 0 ? (
                                                                                <div className="form-group" style={{ marginTop: '12px' }}>
                                                                                    <label className="form-label" style={{
                                                                                        color: 'var(--Gray-900, #181D27)',
                                                                                        fontFamily: 'Plus Jakarta Sans',
                                                                                        fontSize: '16px',
                                                                                        fontStyle: 'normal',
                                                                                        fontWeight: 500,
                                                                                        lineHeight: '20px',
                                                                                        marginBottom: 8
                                                                                    }}>{t('TAX_ADJUSTMENT_DECISION')}</label>
                                                                                    <div
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            height: '40px',
                                                                                            padding: '10px 162px',
                                                                                            flexDirection: 'column',
                                                                                            justifyContent: 'center',
                                                                                            alignItems: 'center',
                                                                                            gap: '10px',
                                                                                            alignSelf: 'stretch',
                                                                                            borderRadius: '4px',
                                                                                            border: '1px dashed var(--Brand-100, #D8D5EA)',
                                                                                            background: 'var(--Background-Light-Base-Main, #FFF)',
                                                                                            cursor: 'pointer',
                                                                                            position: 'relative',
                                                                                            transition: 'border-color 0.2s',
                                                                                        }}
                                                                                        onClick={() => document.getElementById('tax-pdf-upload')?.click()}
                                                                                    >
                                                                                        <input
                                                                                            id="tax-pdf-upload"
                                                                                            type="file"
                                                                                            accept="application/pdf"
                                                                                            onChange={handleFileUpload}
                                                                                            style={{ display: 'none' }}
                                                                                        />
                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                                                <path d="M12 16V8M12 8L8 12M12 8l4 4" stroke="#6750b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                                                <rect x="3" y="3" width="18" height="18" rx="4" stroke="#D8D5EA" strokeWidth="2"/>
                                                                                            </svg>
                                                                                            <span style={{ color: '#6750b8', fontWeight: 500, fontSize: 16 }}>{t('UPLOAD_PDF') || 'Upload PDF'}</span>
                                                                                        </div>
                                                                                        {taxAdjustmentFile && (
                                                                                            <div style={{ marginLeft: 16, fontSize: 14, color: '#333' }}>
                                                                                                <strong>{taxAdjustmentFile.name}</strong>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="form-group tax-disclaimer" style={{ marginTop: '12px' }}>
                                                                                    <label className="checkbox-label" style={{ display: 'flex', alignItems:'start', gap: '8px' }}>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={taxDisclaimerChecked}
                                                                                            onChange={(e) => setTaxDisclaimerChecked(e.target.checked)}
                                                                                            style={{ marginTop: '4px' }}
                                                                                        />
                                                                                        <span style={{ fontSize: '14px' }}>{t("TAX_DECLARATION_1") || "I declare that Invozio is not responsible for my choice of action."}<br/>{t('TAX_DECLARATION_2')}</span>
                                                                                    </label>
                                                                                </div>
                                                                            )}
                                    </div>
                                    
                                  )}
                                 

                                  
                                </div>
                                 
                                
                                                            </>
                                                        )}
                                                    </div>
                                      
                                      

                          {/* Card Footer - Action Buttons in Edit Mode */}
                          {isTaxEditMode && (
                            <div className="card-footer">
                              <div className="form-actions">
                                                                <button
                                                                className="request-change-btn"
                                                                onClick={handleTaxRequestChanges}
                                                                disabled={taxRate && !isNaN(Number(taxRate)) && Number(taxRate) < 10 && Number(taxRate) !== 0 ? false : !isTaxValid()}
                                                                >
                                                                    {t("SAVE_CHANGES")}
                                                                </button>
                                <button className="request-cancel-btn" onClick={handleTaxCancel}>
                                  {t("CANCEL") || "Cancel"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                    </div>
                )}

                                    {activeTab.includes('preferences') && (
                                        <div className="profile-card preferences-card my-sm-0 my-3">
                                            <div className="card-header">
                                                <h2 className="card-title">{t('PREFERENCES_FOR_INVOICING') || 'Preferences for Invoicing'}</h2>
                                            </div>
                                            <div className="card-body preferences-body">
                                                <div className="preferences-content">
                                                    <div className="preferences-form">
                                                        <div className="form-fields">
                                                            {/* Organization Name */}
                                                            <div className="form-group">
                                                                <div className="form-label-wrapper">

                                                                </div>
                                                                <FormField
                                                                    label={t("ORGANIZATION_NAME")}
                                                                    tooltipContent={t("ORGANIZATION_NAME_TOOLTIP")}
                                                                >
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        placeholder={t('ORG_NAME_INVOICE_PLACEHOLDER') || 'The org name may be reflected on the invoice'}
                                                                        value={organizationName}
                                                                        onChange={(e) => setOrganizationName(e.target.value)}
                                                                    />
                                                                </FormField>
                                                            </div>

                                                            {/* Logo Upload */}
                                                            <div className="form-group">
                                                                {/* <div className="form-label-wrapper">
                                                                    <label className="form-label">
                                                                        {t('UPLOAD_YOUR_LOGO_OPTIONAL') || 'Upload Your Logo (Optional)'}
                                                                    </label>
                                                                    <div className="info-icon">
                                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                            <circle cx="10" cy="10" r="9" stroke="#68727D" strokeWidth="1.5" />
                                                                            <path d="M10 14V10M10 6H10.01" stroke="#68727D" strokeWidth="2" strokeLinecap="round" />
                                                                        </svg>
                                                                    </div>
                                                                </div> */}
                                                                <FormField
                                                                    label={t("UPLOAD_YOUR_LOGO")}
                                                                    tooltipContent={t("UPLOAD_YOUR_LOGO_TOOLTIP")}
                                                                // required
                                                                >
                                                                    <div className="logo-upload-wrapper">
                                                                        {/* Show uploaded logo, else show default logo_large.png */}
                                                                        {logoFile ? null : (
                                                                            <div className="current-logo-preview" style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                                                                                <img
                                                                                    src={logoImageSrc || '/assets/logo_large.png'}
                                                                                    alt="Current logo"
                                                                                    style={{ maxWidth: '100px', maxHeight: '50px', objectFit: 'contain' }}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                        {logoFile && (
                                                                            <div className="new-logo-preview" style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                                                                                <img
                                                                                    src={URL.createObjectURL(logoFile)}
                                                                                    alt="New logo preview"
                                                                                    style={{ maxWidth: '100px', maxHeight: '50px', objectFit: 'contain' }}
                                                                                />
                                                                                {/* <span style={{ fontSize: '12px', color: '#27B973', marginLeft: '8px' }}>
                                                                                {t('NEW_LOGO_SELECTED')}
                                                                            </span> */}
                                                                            </div>
                                                                        )}
                                                                        <input
                                                                            type="file"
                                                                            id="logo-upload"
                                                                            accept="image/*"
                                                                            className='d-none'
                                                                            onChange={handleLogoUpload}
                                                                        />
                                                                        <label htmlFor="logo-upload" className="logo-upload-area">
                                                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                                <path d="M3 13L3 15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17L15 17C15.5304 17 16.0391 16.7893 16.4142 16.4142C16.7893 16.0391 17 15.5304 17 15L17 13M13 6L10 3M10 3L7 6M10 3L10 13" stroke="#6750B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                            <span className="upload-text">
                                                                                {logoFile ? logoFile.name : (t('UPLOAD_NEW_LOGO') || 'Upload New Logo')}
                                                                            </span>
                                                                        </label>
                                                                    </div>
                                                                </FormField>
                                                            </div>

                                                            {/* Currency Selection */}
                                                            <div className="form-group">
                                                                <label className="form-label">{t('CHOOSE_CURRENCY') || 'Choose currency'}</label>
                                                                <div className="currency-input-wrapper">
                                                                    <div className="currency-pills">
                                                                        {Array.isArray(selectedCurrencies) && selectedCurrencies.map((currency) => (
                                                                            <div key={currency} className="currency-pill">
                                                                                <span className="currency-text">{currency}</span>
                                                                                <button
                                                                                    className="remove-currency"
                                                                                    onClick={() => handleRemoveCurrency(currency)}
                                                                                    type="button"
                                                                                >
                                                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                                                        <path d="M9 3L3 9M3 3L9 9" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" />
                                                                                    </svg>
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    <select
                                                                        className="currency-input"
                                                                        onChange={(e) => {
                                                                            if (e.target.value) {
                                                                                handleAddCurrency(e.target.value);
                                                                                e.target.value = '';
                                                                            }
                                                                        }}
                                                                        value={userPreferences?.currency || ''}
                                                                    >
                                                                        <option value="" disabled>{t('SELECT_CURRENCY') || 'Select currency'}</option>
                                                                        <option value="USD">{t('USD')}</option>
                                                                        <option value="EUR">{t('EUR')}</option>
                                                                        <option value="GBP">{t('GBP')}</option>
                                                                        <option value="SEK">{t('SEK')}</option>
                                                                        <option value="NOK">{t('NOK')}</option>
                                                                        <option value="DKK">{t('DKK')}</option>
                                                                        <option value="CHF">{t('CHF')}</option>
                                                                        <option value="JPY">{t('JPY')}</option>
                                                                        <option value="CNY">{t('CNY')}</option>
                                                                        <option value="AUD">{t('AUD')}</option>
                                                                        <option value="CAD">{t('CAD')}</option>
                                                                        <option value="AED">{t('AED')}</option>
                                                                        <option value="INR">{t('INR')}</option>
                                                                    </select>
                                                                    <div className="currency-icon">
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            width={15}
                                                                            height={18}
                                                                            viewBox="0 0 15 18"
                                                                            fill="none"
                                                                        >
                                                                            <path
                                                                                d="M10.5 2.16667C10.5 3.08714 8.26142 3.83333 5.5 3.83333C2.73858 3.83333 0.5 3.08714 0.5 2.16667M10.5 2.16667C10.5 1.24619 8.26142 0.5 5.5 0.5C2.73858 0.5 0.5 1.24619 0.5 2.16667M10.5 2.16667V5.5M0.5 2.16667V5.5C0.5 6.42047 2.73858 7.16667 5.5 7.16667C8.26142 7.16667 10.5 6.42047 10.5 5.5M13.8333 7.07183C13.8333 7.9923 11.5948 8.7385 8.83333 8.7385C6.07191 8.7385 3.83333 7.9923 3.83333 7.07183V10.4052M13.8333 7.07183C13.8333 6.34615 12.442 5.7288 10.5 5.5M13.8333 7.07183V10.4052C13.8333 11.3257 11.5948 12.0719 8.83333 12.0719C6.07191 12.0719 3.83333 11.3257 3.83333 10.4052M0.5 11.977C0.5 12.8975 2.73858 13.6437 5.5 13.6437C8.26142 13.6437 10.5 12.8975 10.5 11.977V15.3104C10.5 16.2309 8.26142 16.9771 5.5 16.9771C2.73858 16.9771 0.5 16.2309 0.5 15.3104V11.977ZM0.5 11.977C0.5 11.2513 1.89135 10.634 3.83333 10.4052"
                                                                                stroke="black"
                                                                            />
                                                                        </svg>

                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Action Buttons */}
                                                        <div className="preferences-actions">
                                                            <button className="action-changes-save-btn" onClick={handlePreferencesSave}>
                                                                {t('SAVE_CHANGES')}
                                                            </button>
                                                            <button className="btn-cancel-preferences" onClick={handlePreferencesCancel}>
                                                                {t('CANCEL')}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Preview Section */}

                                                    <InvoicePreview />

                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </Col>
                    </div>
                </div>
        </div>
        {/* Set Password Modal */}
        <SetPasswordModal
            isOpen={isPasswordModalOpen}
            onClose={() => setIsPasswordModalOpen(false)}
        />
        {/* Deactivate Account Modal */}
        <DeactivateAccountModal
            isOpen={isDeactivateModalOpen}
            onClose={() => setIsDeactivateModalOpen(false)}
            onConfirm={handleDeactivateAccount}
        />
        {/* Delete Account Modal */}
        <DeleteAccountModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={handleDeleteAccount}
        />
        {/* Remove Bank Account Modal */}
        <RemoveBankAccountModal
            isOpen={isRemoveBankModalOpen}
            onClose={() => setIsRemoveBankModalOpen(false)}
            onConfirm={handleRemoveBankAccount}
        />
        {/* OTP Verification Modal for Bank Update */}
        <div className={`account-action-modal-overlay ${isOTPModalOpen ? '' : 'hidden'}`} onClick={() => { setIsOTPModalOpen(false); setOTPField(new Array(4).fill('')); setOtpCounter(300); }} style={{ display: isOTPModalOpen ? 'flex' : 'none' }}>
            <div className="account-action-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <h2 className="modal-title">{t('Verify OTP')}</h2>
                    <button
                        className="modal-close-btn"
                            onClick={() => { setIsOTPModalOpen(false); setOTPField(new Array(4).fill('')); setOtpCounter(300); }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="#252525"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        {/* Info Badge */}
                        <div className="sad-message-badge">
                            <span className="emoji">📧</span>
                            <span className="message">{t('VERIFY_CURRENT_EMAIL')}</span>
                        </div>

                        {/* Message */}
                        <div className="modal-message">
                            <p>
                                {t('ENTER_OTP_FROM_EMAIL')}
                            </p>
                            {otpCounter > 0 && otpCounter < 300 && (
                                <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '12px' }}>
                                    {t('RESEND_OTP_IN')} <strong>{otpCounter}s</strong>
                                </p>
                            )}
                        </div>

                        {/* OTP Input Fields */}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '16px' }}>
                            {OTPField.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => { inputRefs.current[index] = el; }}
                                    type="text"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOTPChange(index, e)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Backspace' && !OTPField[index] && index > 0 && inputRefs.current[index - 1]) {
                                            inputRefs.current[index - 1]?.focus();
                                        }
                                    }}
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        textAlign: 'center',
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        border: '2px solid #d0d5dd',
                                        borderRadius: '8px',
                                        outline: 'none',
                                    }}
                                    disabled={otpLoading}
                                />
                            ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="modal-actions-group">
                            <button
                                className="modal-action-btn"
                                onClick={() => submitBankWithOTP(OTPField.join(''))}
                                disabled={otpLoading || OTPField.join('').length < 4}
                                style={{ opacity: (otpLoading || OTPField.join('').length < 4) ? 0.5 : 1, cursor: (otpLoading || OTPField.join('').length < 4) ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{otpLoading ? t('VERIFYING') : t('VERIFY')}</span>
                            </button>
                            <button
                                className="modal-cancel-btn"
                                onClick={handleResendOTP}
                                disabled={otpLoading || otpCounter > 0}
                                style={{ opacity: (otpLoading || otpCounter > 0) ? 0.5 : 1, cursor: (otpLoading || otpCounter > 0) ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{otpCounter > 0 ? `${t('RESEND_IN')} ${otpCounter}s` : t('RESEND')}</span>
                            </button>
                            <button
                                className="modal-cancel-btn"
                                onClick={() => handleCloseOtpModal()}
                                disabled={otpLoading}
                                style={{ opacity: otpLoading ? 0.5 : 1, cursor: otpLoading ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{t('CANCEL')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Email Verification Modal - Step 1: Enter New Email */}
            <div className={`account-action-modal-overlay ${isEmailVerificationModalOpen ? '' : 'hidden'}`} onClick={handleCloseEmailVerification} style={{ display: isEmailVerificationModalOpen && emailVerificationStep === 'enter-email' ? 'flex' : 'none' }}>
                <div className="account-action-modal-container" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="modal-header">
                        <h2 className="modal-title">{t('VERIFY_EMAIL')}</h2>
                        <button
                            className="modal-close-btn"
                            onClick={handleCloseEmailVerification}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="#252525"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        {/* Info Badge */}
                        <div className="sad-message-badge">
                            <span className="emoji">📧</span>
                            <span className="message">{t('CHANGE_EMAIL')}</span>
                        </div>

                        {/* Message */}
                        <div className="modal-message">
                            <p>{t('EMAIL_CHANGE_DESCRIPTION') || 'Enter your new email address and verify it with OTP'}</p>
                            {isGuestInvozioEmail(personalData?.email || '') && (
                                <div style={{
                                    padding: '12px',
                                    backgroundColor: '#fef3cd',
                                    border: '1px solid #fecf33',
                                    borderRadius: '6px',
                                    marginTop: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span style={{ color: '#b45309', fontSize: '16px' }}>⚠️</span>
                                    <span style={{ color: '#b45309', fontSize: '14px', fontWeight: '500' }}>
                                        {t('GUEST_EMAIL_WARNING')}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Step 1: Enter New Email */}
                        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f3f4f6', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                                {t('STEP_1') || 'Step 1'}: {t('ENTER_NEW_EMAIL') || 'Enter your new email address'}
                            </p>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
                                {t('NEW_EMAIL') || 'New Email Address'}
                            </label>

                            <input
                                type="email"
                                value={emailToVerify}
                                onChange={(e) => setEmailToVerify(e.target.value)}
                                placeholder={t('ENTER_NEW_EMAIL') || 'Enter your new email address'}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    fontSize: '14px',
                                    border: '2px solid #d0d5dd',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                                    marginBottom: '12px'
                                }}
                            />

                            <button
                                className="modal-action-btn"
                                onClick={generateOTPForOldEmail}
                                disabled={emailOtpLoading || !emailToVerify}
                                style={{ opacity: (emailOtpLoading || !emailToVerify) ? 0.5 : 1, cursor: (emailOtpLoading || !emailToVerify) ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{emailOtpLoading ? t('SENDING') : t('SEND_OTP')}</span>
                            </button>
                        </div>

                        {/* Cancel Button */}
                        <div className="modal-actions-group" style={{ marginTop: '12px' }}>
                            <button
                                className="modal-cancel-btn"
                                onClick={handleCloseEmailVerification}
                                disabled={emailOtpLoading}
                                style={{ opacity: emailOtpLoading ? 0.5 : 1, cursor: emailOtpLoading ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{t('CANCEL')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Email OTP Verification Modal - Step 2 & 3 */}
            <div className={`account-action-modal-overlay ${emailVerificationStep === 'verify-old-email' || emailVerificationStep === 'verify-new-email' ? '' : 'hidden'}`} onClick={handleCloseEmailVerification} style={{ display: (emailVerificationStep === 'verify-old-email' || emailVerificationStep === 'verify-new-email') ? 'flex' : 'none' }}>
                <div className="account-action-modal-container" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="modal-header">
                        <h2 className="modal-title">{t('VERIFY_OTP')}</h2>
                        <button
                            className="modal-close-btn"
                            onClick={handleCloseEmailVerification}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="#252525"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        {/* Info Badge */}
                        <div className="sad-message-badge">
                            <span className="emoji">📧</span>
                            <span className="message">{t('VERIFY_CURRENT_EMAIL')}</span>
                        </div>

                        {/* Message */}
                        <div className="modal-message">
                            {emailVerificationStep === 'verify-old-email' && (
                                <div>
                                    <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                                        {t('STEP_2') || 'Step 2'}: {t('VERIFY_CURRENT_EMAIL') || 'Verify your current email'}
                                    </p>
                                    <p>{t('ENTER_OTP_FROM_CURRENT_EMAIL') || 'Enter the OTP sent to your current email address'}</p>
                                </div>
                            )}
                            {emailVerificationStep === 'verify-new-email' && (
                                <div>
                                    <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                                        {t('STEP_3') || 'Step 3'}: {t('VERIFY_NEW_EMAIL') || 'Verify your new email'}
                                    </p>
                                    <p>{t('ENTER_OTP_FROM_NEW_EMAIL') || 'Enter the OTP sent to your new email address'}</p>
                                </div>
                            )}
                            {(emailOtpCounter > 0 && emailOtpCounter < 300) && (
                                <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '12px' }}>
                                    {t('RESEND_OTP_IN')} <strong>{emailOtpCounter}s</strong>
                                </p>
                            )}
                        </div>

                        {/* OTP Input Fields */}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '16px' }}>
                            {emailOTPField.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => { emailInputRefs.current[index] = el; }}
                                    type="text"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleEmailOTPChange(index, e)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Backspace' && !emailOTPField[index] && index > 0 && emailInputRefs.current[index - 1]) {
                                            emailInputRefs.current[index - 1]?.focus();
                                        }
                                    }}
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        textAlign: 'center',
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        border: '2px solid #d0d5dd',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        fontFamily: 'Plus Jakarta Sans, sans-serif'
                                    }}
                                    disabled={emailOtpLoading}
                                />
                            ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="modal-actions-group">
                            <button
                                className="modal-action-btn"
                                onClick={() => {
                                    if (emailVerificationStep === 'verify-old-email') {
                                        verifyOldEmailOTP(emailOTPField.join(''));
                                    } else if (emailVerificationStep === 'verify-new-email') {
                                        verifyNewEmailOTP(emailOTPField.join(''));
                                    }
                                }}
                                disabled={emailOtpLoading || emailOTPField.join('').length < 4}
                                style={{ opacity: (emailOtpLoading || emailOTPField.join('').length < 4) ? 0.5 : 1, cursor: (emailOtpLoading || emailOTPField.join('').length < 4) ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{emailOtpLoading ? t('VERIFYING') : t('VERIFY')}</span>
                            </button>
                            <button
                                className="modal-cancel-btn"
                                onClick={handleEmailResendOTP}
                                disabled={emailOtpLoading || emailOtpCounter > 0}
                                style={{ opacity: (emailOtpLoading || emailOtpCounter > 0) ? 0.5 : 1, cursor: (emailOtpLoading || emailOtpCounter > 0) ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{emailOtpCounter > 0 ? `${t('RESEND_IN')} ${emailOtpCounter}s` : t('RESEND')}</span>
                            </button>
                            <button
                                className="modal-cancel-btn"
                                onClick={handleCloseEmailVerification}
                                disabled={emailOtpLoading}
                                style={{ opacity: emailOtpLoading ? 0.5 : 1, cursor: emailOtpLoading ? 'not-allowed' : 'pointer' }}
                            >
                                <span>{t('CANCEL')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default ProfileSettingsPage;





{/* Account Information Card */ }
{/* <div className="profile-card">
                                        <div className="card-header">
                                            <h2 className="card-title">{t('Account Information') || 'Account Information'}</h2>
                                        </div>
                                        <div className="card-body">
                                            <div className="account-info-box">
                                                <div className="account-details">
                                                    <div className="account-icon">
                                                        <svg
                                                            width={32}
                                                            height={32}
                                                            viewBox="0 0 32 32"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M31.6835 16.3683C31.6835 15.2807 31.5953 14.1872 31.4071 13.1172H16.3154V19.2785H24.9577C24.5991 21.2656 23.4468 23.0235 21.7595 24.1405V28.1383H26.9155C29.9432 25.3516 31.6835 21.2362 31.6835 16.3683Z"
                                                                fill="#4285F4"
                                                            />
                                                            <path
                                                                d="M16.3161 32.0004C20.6313 32.0004 24.2705 30.5836 26.922 28.1379L21.766 24.1401C20.3315 25.116 18.4796 25.6686 16.3219 25.6686C12.1478 25.6686 8.60855 22.8526 7.33866 19.0664H2.01807V23.1877C4.73422 28.5906 10.2665 32.0004 16.3161 32.0004Z"
                                                                fill="#34A853"
                                                            />
                                                            <path
                                                                d="M7.33264 19.0676C6.66242 17.0805 6.66242 14.9287 7.33264 12.9416V8.82031H2.01792C-0.251416 13.3413 -0.251416 18.6678 2.01792 23.1889L7.33264 19.0676Z"
                                                                fill="#FBBC04"
                                                            />
                                                            <path
                                                                d="M16.3161 6.33288C18.5972 6.29761 20.8018 7.15596 22.4539 8.73156L27.0219 4.16349C24.1294 1.44734 20.2903 -0.0459547 16.3161 0.00107822C10.2665 0.00107822 4.73422 3.41096 2.01807 8.81974L7.33278 12.941C8.59679 9.14897 12.1419 6.33288 16.3161 6.33288Z"
                                                                fill="#EA4335"
                                                            />
                                                        </svg>

                                                    </div>
                                                    <div className="account-text">
                                                        <p className="account-email">{user?.email || 'kevin@workforve.lv'}</p>
                                                        <p className="account-created">
                                                            {t('Account created') || 'Account created'} : 03 months ago
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="account-actions">
                                                    <button className="set-password-btn" onClick={() => setIsPasswordModalOpen(true)}>
                                                        {t('Set Password') || 'Set Password'}
                                                    </button>
                                                    <button className="set-password-btn" onClick={() => setIsDeactivateModalOpen(true)}>
                                                        {t('Deactivate') || 'Deactivate'}
                                                    </button>
                                                    <button className="set-password-btn" onClick={() => setIsDeleteModalOpen(true)}>
                                                        {t('Delete Account') || 'Delete Account'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div> */}